/**
 * Model kolumn tabeli pozycji dokumentu dla konsumentów układu kolumn (edytor w kreatorze
 * formatki i widok odczytu): stan odczytu pól dodatkowych pozycji i deskryptory kolumn —
 * jedno miejsce zamiast składania tego samego w każdym konsumencie.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EmbeddedTableColumnDescriptor } from '@saas/shared';

import {
  type DocumentItemExtFieldDef,
  useDocumentItemExtFields,
} from '@/lib/documents/use-document-item-ext-fields';

import { documentItemDescriptors } from './document-items-descriptors';

/**
 * Stan odczytu pól dodatkowych pozycji:
 *   - `disabled` — pól nie pobieramy (kreator bez dostępu do danych tenanta): tylko kolumny wbudowane,
 *   - `pending` — pola się ładują (deskryptory na razie tylko wbudowane),
 *   - `error` — odczyt pól się nie udał (deskryptory: wbudowane + ostatnie znane pola z pamięci
 *     podręcznej, chyba że `dropFieldsOnError` — wtedy same wbudowane),
 *   - `ready` — pola załadowane (także gdy tenant nie ma żadnego).
 */
export type DocumentItemColumnsStatus = 'disabled' | 'pending' | 'error' | 'ready';

export interface DocumentItemColumnsModel {
  status: DocumentItemColumnsStatus;
  /** Wbudowane kolumny + pola dodatkowe (przy `ready`; przy `error` — ostatnie znane pola, o ile
   * `dropFieldsOnError` nie jest włączone). */
  descriptors: EmbeddedTableColumnDescriptor[];
  /** Żywe pola dodatkowe pozycji — puste przy `disabled`, przy `pending` jeszcze niezaładowane,
   * przy `error` ostatnie znane z pamięci podręcznej (puste przy `dropFieldsOnError`). */
  extFields: DocumentItemExtFieldDef[];
  /** Przyczyna błędu — tylko przy `status === 'error'`. */
  error: unknown;
}

export interface UseDocumentItemColumnsModelOptions {
  /** `false` = bez zapytania o pola dodatkowe (domyślnie `true`). */
  withExtFields?: boolean;
  /**
   * Co zrobić z polami dodatkowymi, gdy ich (ponowny) odczyt się nie udał (`status` = `error`):
   *   - `false` (domyślnie) — zachować OSTATNIE ZNANE pola z pamięci podręcznej. Karta rekordu
   *     (edycja i odczyt pozycji) nie może przy chwilowym błędzie sieci ukryć kolumny `ext:*`
   *     razem z już wpisanymi wartościami;
   *   - `true` — porzucić je: tylko kolumny wbudowane. Dla kreatora układu, który przy błędzie ma być
   *     wyłącznie podglądem wbudowanych kolumn (zapis układu bez pewnej listy pól zgubiłby ich
   *     kolejność i widoczność).
   * Przy `disabled` pól nie ma w żadnym trybie (zapytania nie ma, więc cache nie jest czytany).
   */
  dropFieldsOnError?: boolean;
}

/**
 * Deskryptory kolumn pozycji dokumentu i stan odczytu pól dodatkowych. `descriptors` jest
 * memoizowane po danych i języku, więc zmienia tożsamość tylko wraz z nimi (bezpieczne w
 * zależnościach hooków).
 */
export function useDocumentItemColumnsModel(
  options: UseDocumentItemColumnsModelOptions = {},
): DocumentItemColumnsModel {
  const withExtFields = options.withExtFields ?? true;
  const dropFieldsOnError = options.dropFieldsOnError ?? false;
  const { t } = useTranslation();
  const { fields, isLoading, isError, error } = useDocumentItemExtFields({
    enabled: withExtFields,
  });
  const status: DocumentItemColumnsStatus = !withExtFields
    ? 'disabled'
    : isError
      ? 'error'
      : isLoading
        ? 'pending'
        : 'ready';
  // Zapytanie bez `enabled` może nadal oddawać dane z pamięci podręcznej (react-query trzyma ostatnie
  // dane) — `disabled` obiecuje „tylko kolumny wbudowane”, więc te dane odrzucamy tutaj. Po nieudanym
  // odświeżeniu (`error`) pola zostają, chyba że konsument woła z `dropFieldsOnError`.
  const keepFields =
    status === 'ready' || status === 'pending' || (status === 'error' && !dropFieldsOnError);
  const liveFields = useMemo(() => (keepFields ? fields : []), [keepFields, fields]);
  const descriptors = useMemo(() => documentItemDescriptors(t, liveFields), [t, liveFields]);
  return { status, descriptors, extFields: liveFields, error };
}
